import axios from 'axios';
import dayjs from 'dayjs';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class KrakenStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  klineWs: ReconnectingWebSocket | undefined;
  tickerWs: ReconnectingWebSocket | undefined;
  tradeWs: ReconnectingWebSocket | undefined;
  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  displayDepth: number;
  fetchDepth = 1000;
  precision: number;
  baseSymbol: string;
  quoteSymbol: string;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.baseSymbol = base.toUpperCase();
    this.quoteSymbol = quote.toUpperCase();

    this.pair = `${this.baseSymbol.toUpperCase()}${this.quoteSymbol.toUpperCase()}`;

    this.bids = [];
    this.asks = [];
    this.displayDepth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;

    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const response = await axios.get<{
        result: Record<
          string,
          {
            base: string;
            quote: string;
            pair_decimals: number;
          }
        >;
      }>('https://api.kraken.com/0/public/AssetPairs');

      const foundSymbol = Object.values(response.data.result).find(
        symbol =>
          symbol.base.toLowerCase() === this.baseSymbol.toLowerCase() &&
          symbol.quote.toLowerCase() === this.quoteSymbol.toLowerCase(),
      );

      if (foundSymbol) {
        this.precision = foundSymbol.pair_decimals;
        this.selectedPrecision = foundSymbol.pair_decimals;
        this.precisionUpdate(foundSymbol.pair_decimals);
      }
    } catch (error) {
      console.log('Kraken ticker error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await axios.get<{
        result: Record<
          string,
          {
            asks: [string, string, string][];
            bids: [string, string, string][];
          }
        >;
      }>('https://api.kraken.com/0/public/Depth', {
        params: {
          pair: this.pair,
          count: this.fetchDepth,
        },
      });

      const data = response.data.result[this.pair];

      if (data) {
        this.asks = data.asks.map(([p, q]) => [p.toString(), q.toString()]);
        this.bids = data.bids.map(([p, q]) => [p.toString(), q.toString()]);

        const orderbook = aggregateOrderbook(
          this.asks,
          this.bids,
          this.selectedPrecision,
          this.displayDepth,
        );

        this.onOrderbookUpdate(orderbook);

        return orderbook;
      }
    } catch (error) {
      console.log('Kraken orderbook error:', error);
    }
  };

  fetchKlines = async ({ limit, interval }: { limit: number; interval: IChartTimeframe }) => {
    const krakenInterval: Partial<Record<IChartTimeframe, number>> = {
      '1m': 1,
      '5m': 5,
      '15m': 15,
      '1h': 60,
      '4h': 240,
      '24h': 1440,
      '168h': 10080,
    };

    const intervalValue = krakenInterval[interval];

    try {
      const response = await axios.get<{
        result: Record<
          string,
          Array<[string, string, string, string, string, string, string, string]>
        >;
      }>('https://api.kraken.com/0/public/OHLC', {
        params: {
          pair: this.pair,
          interval: intervalValue,
          count: limit,
        },
      });

      return response.data.result[this.pair].map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[6],
      ]) as KLine[];
    } catch (error) {
      console.log('Kraken klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { limit: number }): Promise<TradeSocket[]> => {
    try {
      const response = await axios.get<{
        result: Record<string, Array<[string, string, number, string, string, string, number]>>;
      }>('https://api.kraken.com/0/public/Trades', {
        params: {
          pair: this.pair,
          count: limit,
        },
      });

      const trades = (response.data.result[this.pair] || []).reverse().map(item => ({
        price: item[0],
        quantity: item[1],
        timestamp: String(Number(item[2]) * 1000),
        side: item[3] === 's' ? 'sell' : 'buy',
      }));

      return trades.slice(1);
    } catch (error) {
      console.log('Kraken trades error:', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();

    const pairForWs = `${this.baseSymbol.toUpperCase()}/${this.quoteSymbol.toUpperCase()}`;

    const intervalChannel = {
      60000: 1,
      300000: 5,
      900000: 15,
      3600000: 60,
      14400000: 240,
      86400000: 1440,
      604800000: 10080,
    }[interval];

    const ws = new ReconnectingWebSocket('wss://ws.kraken.com/v2', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          method: 'subscribe',
          params: {
            channel: 'book',
            symbol: [pairForWs],
          },
        }),
      );

      ws.send(
        JSON.stringify({
          method: 'subscribe',
          params: {
            channel: 'trade',
            symbol: [pairForWs],
            snapshot: true,
          },
        }),
      );

      ws.send(
        JSON.stringify({
          method: 'subscribe',
          params: {
            channel: 'ohlc',
            symbol: [pairForWs],
            interval: intervalChannel,
          },
        }),
      );

      ws.send(
        JSON.stringify({
          method: 'subscribe',
          params: {
            channel: 'ticker',
            symbol: [pairForWs],
          },
        }),
      );
    };

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.channel === 'book') {
          const pair = data.data.at(0);

          const asks = mergeOrderBook(
            this.asks,
            pair.asks.map((ask: { price: number; qty: number }) => [ask.price, ask.qty]),
          ).sort(sortOrderbookFunc);

          const bids = mergeOrderBook(
            this.bids,
            pair.bids.map((bid: { price: number; qty: number }) => [bid.price, bid.qty]),
          ).sort(sortOrderbookFunc);

          this.asks = asks;
          this.bids = bids;

          this.onOrderbookUpdate(
            aggregateOrderbook(
              asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.selectedPrecision,
            ),
          );
        }

        if (data.channel === 'ohlc') {
          const { open, high, low, close, volume, timestamp } = data.data.at(-1);

          this.klineUpdate([
            Math.floor(dayjs.utc(timestamp).valueOf() / 1000),
            normalizeNumber(open),
            normalizeNumber(high),
            normalizeNumber(low),
            normalizeNumber(close),
            normalizeNumber(volume),
          ]);
        }

        if (data.channel === 'trade') {
          const trade = data.data.at(-1);

          this.onTradeUpdate({
            price: trade.price,
            quantity: trade.qty,
            timestamp: String(Math.floor(dayjs.utc(trade.timestamp).valueOf() / 1000)),
            side: trade.side,
          });
        }

        if (data.channel === 'ticker') {
          const { change, low, last, high, volume, ask_qty, bid_qty } = data.data.at(0);

          const averagePrice = (ask_qty + bid_qty) / 2;
          const quoteVolume = volume * (last || averagePrice);

          this.onTickerUpdate({
            baseVolume: normalizeNumber(volume),
            change: normalizeNumber(change),
            close: normalizeNumber(last),
            high: normalizeNumber(high),
            last: last.toFixed(this.precision),
            low: normalizeNumber(low),
            quoteVolume: normalizeNumber(quoteVolume),
          });
        }
      } catch (error) {
        console.log('Kraken websocket error:', error);
      }
    };

    this.ws = ws;
  };

  closeWebSocket() {
    if (this.ws) this.ws.close();
  }

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    const orderbook = aggregateOrderbook(
      this.asks,
      this.bids,
      this.selectedPrecision,
      this.displayDepth,
    );

    this.onOrderbookUpdate(orderbook);
  };

  setOrderbookDepth(depth: number) {
    this.displayDepth = depth;
    this.fetchOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new KrakenStrategy(args);
    await instance.fetch();
    return instance;
  };
}
